import { setLocale } from 'react-redux-i18n';
import _ from 'lodash';
import api from '../api/apcApi';
import { supportedLocales, fallbackLocale } from '../i18n/config';
import {
  SET_OTHER_ASSOCIATED_ACCOUNT,
  FETCH_ACCESS_REQUESTS,
  FETCHED_ACCESS_REQUESTS,
  FETCHED_COMPANY_SUGGESTIONS,
  THROW_ERROR,
  SELECT_COMPANY,
} from '../constants';

export const setLocaleWithFallback = (locale) => async (dispatch) => {
  const finalLocale = Object.values(supportedLocales).includes(locale)
    ? locale
    : fallbackLocale;
  // Store the preferred locale in session storage.
  localStorage.setItem('i18n_lang', finalLocale);

  dispatch(setLocale(finalLocale));
};

// Initialization Actions
export const initializeApp = (history) => async (dispatch) => {
  // Before we do any searches, let's see if we have an admin query parameter.
  const urlParams = new URLSearchParams(window.location.search);
  const pathName = window.location.pathname;
  const isContactReg = pathName == "/" || pathName.toLowerCase().indexOf("/contactreg") > -1;
  const isReg = pathName.toLowerCase().indexOf("/registration") > -1;
  
  // If we have ?view=requests, go to the admin requests screen.
  if ((isContactReg || isReg) && urlParams.has('view') && urlParams.get('view') === 'requests') {
    // Grab the admin requests, navigate to the admin screen.
    dispatch({ type: FETCH_ACCESS_REQUESTS });

    try {
      const response = await api.get('/v1/contact/approvals?programType=APC',
        {
          headers: {
            Authorization: `Bearer ${window.adobeIMS.getAccessToken().token}`,
            'x-api-key': window.apcConfig.adobeIO.apiKey,
            // Prefer: 'example=example',
          },
        });

      if (response.data.contacts) {
        dispatch({ type: FETCHED_ACCESS_REQUESTS, payload: response.data.contacts });
        history.push('/admin');
      } else {
        dispatch({ type: THROW_ERROR, payload: { messagePath: 'workflow.error_page.admin_fetch_requests_problem', fatal: true } });
        window.console.error('Fatal Error: Problem with fetching approval requests, no access requests payload.', response);
        if (window.newrelic) {
          window.newrelic.noticeError('Missing object key: accessRequests',
            {
              page: 'Init',
              api: '/v1/contact/approvals',
              action: 'fetch',
              fatal: true,
              user: window.adobeIMS.getUserProfile().email,
              response: response.data,
            });
        }
      }
    } catch (error) {
      dispatch({ type: THROW_ERROR, payload: { messagePath: 'workflow.error_page.admin_fetch_requests_problem', fatal: true } });
      window.console.error('Fatal Error: Problem with fetching approval requests, API call failed.', error);
      if (window.newrelic) {
        window.newrelic.noticeError(error.message,
          {
            page: 'Init',
            api: '/v1/contact/approvals',
            action: 'fetch',
            fatal: true,
            user: window.adobeIMS.getUserProfile().email,
          });
      }
    }
  } else if (!isContactReg && pathName.length > 1) {
    let pathNames = pathName.split("/");
    let finalPath = "";
    do {
        finalPath = pathNames.pop();
    } while(!finalPath);
    history.push("/" + (finalPath ? finalPath : "registration"));
    return;
  } else {
    // Otherwise, run the e-mail search
    try {
      const contactSearch = await api.get('/v1/findpartneremail?programType=APC',
        {
          headers:
          {
            Authorization: `Bearer ${window.adobeIMS.getAccessToken().token}`,
            'x-api-key': window.apcConfig.adobeIO.apiKey,
            // Prefer: 'example=usingPublicDomain', Example response
          },
        });

      const result = contactSearch.data;

      /**
       *  Workflow Scenarios
       */

      /* User using a banned domain. */
      if (result.isBannedDomain) {
        history.push('/error_banned_domain');
        return;
      }

      /** E-mail Doesn't Exist */

      /* No accounts found */
      if (!result.emailExists && !result.isPublicDomain && !result.accounts.length) {
        history.push('/error_no_contact_exists');
        return;
      }

      /* Using a public domain e-mail address */
      if (!result.emailExists && result.isPublicDomain) {
        history.push('/company_search_public');
        return;
      }

      /* Using a company e-mail address */
      if (!result.emailExists && !result.isPublicDomain && result.accounts.length) {
        dispatch({
          type: FETCHED_COMPANY_SUGGESTIONS,
          payload: _.sortBy(result.accounts, [(o) => o.name]),
        });
        history.push('/company_select');
        return;
      }

      /** E-mail Exists */

      /* Associated with a non-partner account */
      if (result.emailExists && !result.isPartnerContact) {
        // Display error message with option to contact helpdesk.
        history.push('/error_non_partner_account');
        return;
      }

      /* Rejected partner contact status */
      if (result.emailExists && result.isPartnerContact && result.status === 'Rejected') {
        // Display error message with option to contact helpdesk.
        history.push('/error_contact_rejected');
        return;
      }

      /* Pending partner contact status */
      if (result.emailExists && result.isPartnerContact && result.status === 'Pending') {
        // Send to the pending approval thank you page.
        history.push('/thank_you_public');
        return;
      }

      /* Invited contact and hasn't accepted invitation */
      if (result.emailExists && result.isPartnerContact && result.invited && result.programType === 'APC') {
        const company = {
          recId: result.accountId,
          authKey: result.authKey,
        };
        dispatch({ type: SELECT_COMPANY, payload: company });
        // Send to Personal Info page since we already have the company.
        history.push('/personal_information');
        return;
      }

      /* Contact exists with partially registered account */
      if (result.emailExists && result.isPartnerContact && result.partialRegistration) {
        // Redirect to continue enrollment page.
        window.location.replace(window.apcConfig.partner.continueEnrollment);
        return;
      }

      /* Already completed APC registration */
      if (result.emailExists && result.isPartnerContact && result.programType === 'APC') {
        history.push('/error_already_registered');
        return;
      }

      /* Associated with non-APC partner account */
      if (result.emailExists && result.isPartnerContact && result.programType !== 'APC') {
        dispatch({
          type: SET_OTHER_ASSOCIATED_ACCOUNT,
          payload: result.programType,
        });
        history.push('/error_contact_exists');
        return;
      }

      /**
       * No matching scenario
       */

      dispatch({ type: THROW_ERROR, payload: { messagePath: 'workflow.error_page.initial_search_problem', fatal: true } });
      window.console.error('Fatal Error: Problem with initial company search, no scenario matched.', contactSearch);
      if (window.newrelic) {
        window.newrelic.noticeError('Unknown scenario for initial search',
          {
            page: 'Init',
            api: '/v1/findpartneremail',
            fatal: true,
            user: window.adobeIMS.getUserProfile().email,
            response: contactSearch.data,
          });
      }
    } catch (error) {
      dispatch({ type: THROW_ERROR, payload: { messagePath: 'workflow.error_page.initial_search_problem', fatal: true } });
      window.console.error('Fatal Error: Problem with initial company search, api call failed.', error);
      if (window.newrelic) {
        window.newrelic.noticeError(error.message,
          {
            page: 'Init',
            api: '/v1/findpartneremail',
            fatal: true,
            user: window.adobeIMS.getUserProfile().email,
          });
      }
    }
  }
};
